import { useState, useMemo } from 'react'
import {
    Box,
    Typography,
    Card,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Avatar,
    Divider,
    CircularProgress
} from '@mui/material'
import {
    Save as SaveIcon,
    Cancel as CancelIcon,
    PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material'
import CustomDatePicker from '../CustomDatePicker'
import { PrimaryButton, SecondaryButton } from '..'
import { router } from '@inertiajs/react'
import { useAuth } from '../../hooks/useAuth'
import axiosClient from '../../services/axiosClient'


interface ProfileData {
    first_name: string
    last_initial: string
    photo: string | null
    profile_pic_url: string | null
    short_intro: string
    occupation: string
    employer: string
    rental_type?: string
    tour_preference?: string
    budget: string
    bedrooms: string
    bathrooms: string
    desired_move_in: string
    lease_length: string
    pets: string
    occupants: string
    neighborhoods: string
    additionalInfo: string
    housingVoucher: string
    numDogs: string
    numCats: string
}

interface ProfileEditProps {
    profileData: ProfileData
    onCancel: () => void
}

export default function ProfileEdit({ profileData, onCancel }: ProfileEditProps) {
    const [formData, setFormData] = useState<ProfileData>(profileData)
    const [errors, setErrors] = useState<{ [key: string]: string }>({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { renter } = useAuth()

    // Memoize date value to prevent DatePicker from resetting
    const memoizedDateValue = useMemo(() => {
        return formData.desired_move_in ? new Date(formData.desired_move_in) : null;
    }, [formData.desired_move_in]);

    const handleInputChange = (field: keyof ProfileData, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }))
        }
    }

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {}

        if (!formData.first_name.trim()) {
            newErrors.first_name = 'First name is required'
        }

        if (!formData.short_intro.trim()) {
            newErrors.short_intro = 'Short introduction is required'
        } else if (formData.short_intro.length > 500) {
            newErrors.short_intro = 'Introduction must be 500 characters or less'
        }

        if (!formData.bedrooms || Number(formData.bedrooms) < 0) {
            newErrors.bedrooms = 'Please enter a valid number of bedrooms'
        }

        if (!formData.bathrooms || Number(formData.bathrooms) < 0) {
            newErrors.bathrooms = 'Please enter a valid number of bathrooms'
        }

        if (!formData.desired_move_in) {
            newErrors.desired_move_in = 'Move-in date is required'
        }

        if (!formData.lease_length) {
            newErrors.lease_length = 'Lease length is required'
        }

        if (!formData.occupants || Number(formData.occupants) < 1) {
            newErrors.occupants = 'Please enter at least 1 occupant'
        }

        if (!formData.occupation.trim()) {
            newErrors.occupation = 'Occupation is required'
        }

        if (!formData.employer.trim()) {
            newErrors.employer = 'Employer is required'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSave = async () => {
        if (!validateForm()) {
            // Find the first field with an error and scroll to it
            const firstErrorField = Object.keys(errors).find(field => errors[field])
            if (firstErrorField) {
                const fieldElement = document.querySelector(`[name="${firstErrorField}"]`) ||
                                   document.querySelector(`#${firstErrorField}`) ||
                                   document.querySelector(`[data-field="${firstErrorField}"]`)
                if (fieldElement) {
                    fieldElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    })
                    // Focus the field after scrolling
                    setTimeout(() => {
                        if (fieldElement instanceof HTMLElement) {
                            fieldElement.focus()
                        }
                    }, 500)
                }
            }
            return
        }

        setIsSubmitting(true)

        try {
            const response = await axiosClient.post(`/renters/${renter?.id}/profile/update`, formData)
            const result = response.data

            if (result.success) {
                // Success - redirect to profile page
                router.visit(`/renters/${renter?.id}/profile`)
            } else {
                // Handle validation errors from server
                if (result.error && Array.isArray(result.error)) {
                    const serverErrors: { [key: string]: string } = {}
                    result.error.forEach((error: string) => {
                        // Map server errors to form fields
                        if (error.includes('name')) {
                            serverErrors.first_name = error
                        } else if (error.includes('short_intro')) {
                            serverErrors.short_intro = error
                        } else if (error.includes('occupation')) {
                            serverErrors.occupation = error
                        } else if (error.includes('employer')) {
                            serverErrors.employer = error
                        }
                        // Add more mappings as needed
                    })
                    setErrors(serverErrors)
                } else {
                    setErrors({ general: result.error || 'An error occurred while saving' })
                }
            }
        } catch (error) {
            console.error('Error saving profile:', error)
            setErrors({ general: 'An error occurred while saving. Please try again.' })
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="body1" color="grey.600">
                    Update your profile information
                </Typography>
            </Box>

            <Card sx={{ p: 3 }}>
                {/* Profile Photo Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar
                        src={formData.profile_pic_url || formData.photo || undefined}
                        sx={{
                            width: 80,
                            height: 80,
                            bgcolor: 'grey.300',
                            fontSize: '2rem'
                        }}
                    >
                        {!formData.profile_pic_url && !formData.photo && (formData.first_name?.[0] || '?')}
                    </Avatar>
                    <Box>
                        <Button
                            variant="outlined"
                            startIcon={<PhotoCameraIcon />}
                            size="small"
                            disabled
                        >
                            Update Photo
                        </Button>
                    </Box>
                </Box>

                {/* Basic Info Section */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Basic Information
                </Typography>

                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="First Name *"
                            name="first_name"
                            value={formData.first_name}
                            onChange={(e) => handleInputChange('first_name', e.target.value)}
                            error={!!errors.first_name}
                            helperText={errors.first_name}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Last Initial"
                            value={formData.last_initial}
                            onChange={(e) => handleInputChange('last_initial', e.target.value)}
                            placeholder="D"
                            inputProps={{ maxLength: 1 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="A short intro goes a long way *"
                            name="short_intro"
                            value={formData.short_intro}
                            onChange={(e) => handleInputChange('short_intro', e.target.value)}
                            error={!!errors.short_intro}
                            helperText={errors.short_intro || `${formData.short_intro.length}/500 characters`}
                            placeholder="Break the ice, share a little about yourself"
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Employment Section */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Employment
                </Typography>

                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Occupation *"
                            name="occupation"
                            value={formData.occupation}
                            onChange={(e) => handleInputChange('occupation', e.target.value)}
                            error={!!errors.occupation}
                            helperText={errors.occupation}
                            placeholder="e.g., Nurse"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Employer *"
                            name="employer"
                            value={formData.employer}
                            onChange={(e) => handleInputChange('employer', e.target.value)}
                            error={!!errors.employer}
                            helperText={errors.employer}
                            placeholder="e.g., Johns Hopkins Hospital"
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Rental Preferences Section */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Rental Preferences
                </Typography>

                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Rental Type</InputLabel>
                            <Select
                                value={formData.rental_type || ''}
                                onChange={(e) => handleInputChange('rental_type' as any, e.target.value as string)}
                                label="Rental Type"
                            >
                                <MenuItem value="entire_place">Entire Place</MenuItem>
                                <MenuItem value="private_room">Private Room</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Tour Preference</InputLabel>
                            <Select
                                value={formData.tour_preference || ''}
                                onChange={(e) => handleInputChange('tour_preference' as any, e.target.value as string)}
                                label="Tour Preference"
                            >
                                <MenuItem value="in_person">In person</MenuItem>
                                <MenuItem value="virtual">Virtual</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Bedrooms *"
                            name="bedrooms"
                            value={formData.bedrooms}
                            onChange={(e) => handleInputChange('bedrooms', e.target.value)}
                            error={!!errors.bedrooms}
                            helperText={errors.bedrooms}
                            inputProps={{ min: 0 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Bathrooms *"
                            name="bathrooms"
                            value={formData.bathrooms}
                            onChange={(e) => handleInputChange('bathrooms', e.target.value)}
                            error={!!errors.bathrooms}
                            helperText={errors.bathrooms}
                            inputProps={{ min: 0 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            label="Desired Move-in Date *"
                            value={memoizedDateValue}
                            onChange={(newValue) => {
                                const formatted = newValue ? newValue.toISOString().split('T')[0] : ''
                                handleInputChange('desired_move_in', formatted)
                            }}
                            error={!!errors.desired_move_in}
                            helperText={errors.desired_move_in}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth error={!!errors.lease_length}>
                            <InputLabel>Lease Length *</InputLabel>
                            <Select
                                value={formData.lease_length}
                                onChange={(e) => handleInputChange('lease_length', e.target.value)}
                                label="Lease Length *"
                            >
                                <MenuItem value="12 month">12 month lease</MenuItem>
                                <MenuItem value="6 month">6 month lease</MenuItem>
                                <MenuItem value="3 month">3 month lease</MenuItem>
                                <MenuItem value="Month to month">Month to Month</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Number of Occupants *"
                            name="occupants"
                            value={formData.occupants}
                            onChange={(e) => handleInputChange('occupants', e.target.value)}
                            error={!!errors.occupants}
                            helperText={errors.occupants}
                            inputProps={{ min: 1 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Preferred Neighborhoods"
                            value={formData.neighborhoods}
                            onChange={(e) => handleInputChange('neighborhoods', e.target.value)}
                            placeholder="Fells Point, Mount Vernon, Canton..."
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Additional Information"
                            value={formData.additionalInfo}
                            onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                            placeholder="Anything else you'd like landlords to know..."
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Pet  Section */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Pets
                </Typography>

                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Number of Dogs"
                            value={formData.numDogs}
                            onChange={(e) => handleInputChange('numDogs', e.target.value)}
                            inputProps={{ min: 0 }}
                            placeholder="0"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Number of Cats"
                            value={formData.numCats}
                            onChange={(e) => handleInputChange('numCats', e.target.value)}
                            inputProps={{ min: 0 }}
                            placeholder="0"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Housing Voucher</InputLabel>
                            <Select
                                value={formData.housingVoucher}
                                onChange={(e) => handleInputChange('housingVoucher', e.target.value)}
                                label="Housing Voucher"
                            >
                                <MenuItem value="yes">Yes</MenuItem>
                                <MenuItem value="no">No</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                    <SecondaryButton
                        startIcon={<CancelIcon />}
                        onClick={onCancel}
                        sx={{ flex: 1 }}
                    >
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={isSubmitting}
                        sx={{ flex: 1 }}
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </PrimaryButton>
                </Box>
            </Card>
        </Box>
    )
}
